import { useState, useEffect } from 'react';
import { X, Plus, Minus, Save, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui';
import { sgmDefaultTemplate } from '@/data/templates';
import type { PhaseTemplate, TaskTemplate, Phase, FunctionalTask } from '@/types';

type FunctionalNode = '项目立项' | '数据发布' | '首次造车' | '可销售车';

interface ComponentTimingTemplate {
  id: string;
  oem: string;
  product: string;
  description: string;
  phases: PhaseTemplate[];
  created_at: string;
  updated_at: string;
}

interface ComponentTimingTemplateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingTemplate: ComponentTimingTemplate | null;
}

const functionalNodes: FunctionalNode[] = ['项目立项', '数据发布', '首次造车', '可销售车'];

const phaseOptions: { value: Phase; label: string }[] = [
  { value: 'supplierSelection', label: '阶段1：商务定点阶段' },
  { value: 'stylingConcept', label: '阶段2：造型设计阶段' },
  { value: 'detailedDesign', label: '阶段3：工程设计阶段' },
  { value: 'toolingLine', label: '阶段4：Tooling开发阶段' },
  { value: 'productDevelopment', label: '阶段5：产品开发阶段' },
];

const functionalTaskOptions: { value: FunctionalTask; label: string }[] = [
  { value: '-', label: '-' },
  { value: '产品T0', label: '产品T0' },
  { value: '产品试验', label: '产品试验' },
  { value: '电子试验', label: '电子试验' },
  { value: '法规认证', label: '法规认证' },
  { value: '皮纹加工', label: '皮纹加工' },
];

const generateId = () => {
  return Date.now().toString() + Math.random().toString(36).substr(2, 9);
};

export function ComponentTimingTemplateEditModal({
  isOpen,
  onClose,
  onSave,
  editingTemplate
}: ComponentTimingTemplateEditModalProps) {
  const [oem, setOem] = useState('');
  const [product, setProduct] = useState('');
  const [description, setDescription] = useState('');
  const [phases, setPhases] = useState<PhaseTemplate[]>([]);
  const [saving, setSaving] = useState(false);

  const isNewTemplate = !editingTemplate?.id;

  useEffect(() => {
    if (isOpen) {
      if (editingTemplate) {
        setOem(editingTemplate.oem);
        setProduct(editingTemplate.product);
        setDescription(editingTemplate.description);
        setPhases(JSON.parse(JSON.stringify(editingTemplate.phases)));
      } else {
        setOem('SGM');
        setProduct('Exterior Lighting');
        setDescription('');
        setPhases(JSON.parse(JSON.stringify(sgmDefaultTemplate)));
      }
    }
  }, [isOpen, editingTemplate]);

  const getAllTaskNames = (): string[] => {
    const names: string[] = [];
    phases.forEach(phase => {
      phase.tasks.forEach(task => {
        if (task.name) {
          names.push(task.name);
        }
      });
    });
    return names;
  };

  const getStartNodeOptions = (currentPhaseIndex: number, currentTaskIndex: number): (FunctionalNode | string)[] => {
    const options: (FunctionalNode | string)[] = [...functionalNodes];
    
    phases.forEach((phase, phaseIdx) => {
      phase.tasks.forEach((task, taskIdx) => {
        if (phaseIdx < currentPhaseIndex || (phaseIdx === currentPhaseIndex && taskIdx < currentTaskIndex)) {
          if (task.name && !options.includes(task.name)) {
            options.push(task.name);
          }
        }
      });
    });
    
    return options;
  };

  const getEndNodeOptions = (currentPhaseIndex: number, currentTaskIndex: number): (FunctionalNode | string)[] => {
    const options: (FunctionalNode | string)[] = [...functionalNodes];
    
    phases.forEach((phase, phaseIdx) => {
      phase.tasks.forEach((task, taskIdx) => {
        if (phaseIdx < currentPhaseIndex || (phaseIdx === currentPhaseIndex && taskIdx < currentTaskIndex)) {
          if (task.name && !options.includes(task.name)) {
            options.push(task.name);
          }
        }
      });
    });
    
    return options;
  };

  const handleTaskChange = (phaseIndex: number, taskIndex: number, field: keyof TaskTemplate, value: any) => {
    const newPhases = [...phases];
    const task = { ...newPhases[phaseIndex].tasks[taskIndex] };
    
    if (field === 'functionalTask') {
      task.functionalTask = value === '-' ? undefined : value;
    } else if (field === 'reviewTask') {
      task.reviewTask = value ? true : undefined;
    } else if (field === 'startNode') {
      if (value === '无') {
        task.startNode = '无';
        task.predecessor = undefined;
      } else {
        task.startNode = value || undefined;
        task.predecessor = undefined;
      }
      
      if (task.startNode && task.startNode !== '无' && task.endNode) {
        task.duration = null;
        task.minDuration = null;
      }
    } else if (field === 'endNode') {
      task.endNode = value || undefined;
      
      if (task.startNode && task.startNode !== '无' && task.endNode) {
        task.duration = null;
        task.minDuration = null;
      }
    } else if (field === 'startNodeOffset') {
      (task as any).startNodeOffset = value ? parseInt(value) : undefined;
    } else if (field === 'endNodeOffset') {
      (task as any).endNodeOffset = value ? parseInt(value) : undefined;
    } else if (field === 'offsetWeeks') {
      task.offsetWeeks = value ? parseInt(value) : undefined;
    } else {
      (task as any)[field] = value;
    }
    
    newPhases[phaseIndex].tasks[taskIndex] = task;
    setPhases(newPhases);
  };

  const handleAddTask = (phaseIndex: number, taskIndex: number) => {
    const newPhases = [...phases];
    const newTask: TaskTemplate = {
      name: '',
      duration: 14,
      minDuration: 14,
      subTask: newPhases[phaseIndex].tasks[taskIndex]?.subTask || '',
      description: '',
      functionalTask: undefined,
      reviewTask: undefined,
    };
    newPhases[phaseIndex].tasks.splice(taskIndex + 1, 0, newTask);
    setPhases(newPhases);
  };

  const handleRemoveTask = (phaseIndex: number, taskIndex: number) => {
    const newPhases = [...phases];
    if (newPhases[phaseIndex].tasks.length > 1) {
      newPhases[phaseIndex].tasks.splice(taskIndex, 1);
      setPhases(newPhases);
    }
  };

  const handleAddPhase = () => {
    const newPhase: PhaseTemplate = {
      name: `阶段${phases.length + 1}`,
      phase: `phase_${generateId()}` as Phase,
      tasks: [{
        name: '',
        duration: 14,
        minDuration: 14,
        subTask: '',
        description: '',
        functionalTask: undefined,
        reviewTask: undefined,
      }]
    };
    setPhases([...phases, newPhase]);
  };

  const handleRemovePhase = (phaseIndex: number) => {
    if (phases.length > 1) {
      const newPhases = phases.filter((_, idx) => idx !== phaseIndex);
      setPhases(newPhases);
    }
  };

  const handlePhaseNameChange = (phaseIndex: number, value: string) => {
    const newPhases = [...phases];
    newPhases[phaseIndex].name = value;
    setPhases(newPhases);
  };

  const normalizePredecessors = () => {
    const newPhases = phases.map(phase => ({
      ...phase,
      tasks: phase.tasks.map((task, taskIndex, tasks) => {
        if (!task.startNode) {
          const prevTask = taskIndex > 0 ? tasks[taskIndex - 1] : null;
          if (prevTask && prevTask.subTask === task.subTask) {
            return { ...task, predecessor: prevTask.name };
          }
        }
        return task;
      })
    }));
    return newPhases;
  };

  const handleSave = async () => {
    if (!product.trim()) {
      alert('请输入Product名称');
      return;
    }

    for (const phase of phases) {
      for (const task of phase.tasks) {
        if (!task.name?.trim()) {
          alert('请填写所有任务名称');
          return;
        }
      }
    }

    setSaving(true);
    try {
      const storedSession = localStorage.getItem('session');
      let token: string | null = null;
      
      if (storedSession) {
        try {
          const session = JSON.parse(storedSession);
          token = session?.access_token || null;
        } catch {
          token = null;
        }
      }

      if (!token) {
        alert('未登录，请先登录');
        setSaving(false);
        return;
      }

      const url = '/api/templates';
      const method = editingTemplate?.id ? 'PUT' : 'POST';
      
      const normalizedPhases = normalizePredecessors();
      
      const body = {
        type: 'component',
        id: editingTemplate?.id || undefined,
        oem: oem.trim(),
        product: product.trim(),
        description: description.trim(),
        phases: normalizedPhases
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        onSave();
      } else {
        const error = await response.json();
        console.error('Save error:', error);
        alert(error.error || '保存失败');
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">
            {isNewTemplate ? '创建零部件时间计划模板' : '编辑零部件时间计划模板'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={oem}
                  onChange={(e) => setOem(e.target.value)}
                  placeholder="例如: SGM, VW, BMW"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder="例如: Exterior Lighting"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="模板描述"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  阶段与任务列表
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={handleAddPhase}
                >
                  添加阶段
                </Button>
              </div>

              <div className="space-y-6">
                {phases.map((phase, phaseIndex) => (
                  <div key={phaseIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                      <input
                        type="text"
                        value={phase.name}
                        onChange={(e) => handlePhaseNameChange(phaseIndex, e.target.value)}
                        className="font-semibold text-gray-800 bg-transparent border-none focus:outline-none focus:ring-0 flex-1"
                        placeholder="阶段名称"
                      />
                      {phases.length > 1 && (
                        <button
                          onClick={() => handleRemovePhase(phaseIndex)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="删除阶段"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[8%]">功能任务</th>
                            <th className="text-center px-3 py-2 font-semibold text-gray-700 w-[5%]">评审</th>
                            <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[10%]">一级任务</th>
                            <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[10%]">二级子任务</th>
                            <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[7%]">开始节点</th>
                            <th className="text-center px-3 py-2 font-semibold text-gray-700 w-[4%]">Offset</th>
                            <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[7%]">完成节点</th>
                            <th className="text-center px-3 py-2 font-semibold text-gray-700 w-[4%]">Offset</th>
                            <th className="text-center px-3 py-2 font-semibold text-gray-700 w-[5%]">最短工期</th>
                            <th className="text-center px-3 py-2 font-semibold text-gray-700 w-[5%]">最长工期</th>
                            <th className="text-center px-3 py-2 font-semibold text-gray-700 w-[5%]">同步工期</th>
                            <th className="text-center px-3 py-2 font-semibold text-gray-700 w-[5%]">操作</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {phase.tasks.map((task, taskIndex) => {
                            const hasBothNodes = task.startNode && task.startNode !== '无' && task.endNode;
                            const startNodeOptions = getStartNodeOptions(phaseIndex, taskIndex);
                            const endNodeOptions = getEndNodeOptions(phaseIndex, taskIndex);
                            const isFirstTaskInSubTask = taskIndex === 0 || phase.tasks[taskIndex - 1].subTask !== task.subTask;
                            
                            return (
                              <tr key={taskIndex} className="hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <select
                                    value={task.functionalTask || '-'}
                                    onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'functionalTask', e.target.value as FunctionalTask)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                                  >
                                    {functionalTaskOptions.map(option => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={task.reviewTask || false}
                                    onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'reviewTask', e.target.checked)}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={task.subTask || ''}
                                    onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'subTask', e.target.value)}
                                    placeholder="一级任务"
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={task.name}
                                    onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'name', e.target.value)}
                                    placeholder="任务名称"
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <select
                                    value={task.startNode || ''}
                                    onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'startNode', e.target.value || undefined)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                                  >
                                    <option value="">前序完成</option>
                                    <option value="无">无</option>
                                    {startNodeOptions.map(node => (
                                      <option key={node} value={node}>{node}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="pl-0 pr-3 py-2 text-center">
                                  <input
                                    type="number"
                                    value={task.startNodeOffset || ''}
                                    onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'startNodeOffset', e.target.value)}
                                    placeholder="0"
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-center"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <select
                                    value={task.endNode || ''}
                                    onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'endNode', e.target.value || undefined)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                                  >
                                    <option value="">无</option>
                                    {endNodeOptions.map(node => (
                                      <option key={node} value={node}>{node}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="pl-0 pr-3 py-2 text-center">
                                  <input
                                    type="number"
                                    value={task.endNodeOffset || ''}
                                    onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'endNodeOffset', e.target.value)}
                                    placeholder="0"
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-center"
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {hasBothNodes ? (
                                    <span className="text-orange-500 font-medium">N/A</span>
                                  ) : (
                                    <input
                                      type="number"
                                      value={task.minDuration ? Math.round(task.minDuration / 7) : ''}
                                      onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'minDuration', e.target.value ? parseInt(e.target.value) * 7 : undefined)}
                                      placeholder="周"
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-center"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {hasBothNodes ? (
                                    <span className="text-orange-500 font-medium">N/A</span>
                                  ) : (
                                    <input
                                      type="number"
                                      value={task.duration ? Math.round(task.duration / 7) : ''}
                                      onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'duration', e.target.value ? parseInt(e.target.value) * 7 : null)}
                                      placeholder="周"
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-center"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {hasBothNodes ? (
                                    <span className="text-gray-400">-</span>
                                  ) : (
                                    <input
                                      type="number"
                                      value={task.overlapWeeks || ''}
                                      onChange={(e) => handleTaskChange(phaseIndex, taskIndex, 'overlapWeeks', e.target.value ? parseInt(e.target.value) : undefined)}
                                      placeholder="0"
                                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-center"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center justify-center space-x-1">
                                    <button
                                      onClick={() => handleAddTask(phaseIndex, taskIndex)}
                                      className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                                      title="在此行后插入"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleRemoveTask(phaseIndex, taskIndex)}
                                      disabled={phase.tasks.length <= 1}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="删除此行"
                                    >
                                      <Minus className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="primary"
            leftIcon={<Save className="w-4 h-4" />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>
    </div>
  );
}
